// src/pages/notifications/ContactDetailPage.tsx
// Contact detail view with verification actions.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsContact, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, Alert, Badge, ErrorAlert,
} from '@react/components';
import { formatContactType } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import type { Contact } from '@core/types/notifications/Contact';

export const ContactDetailPage = observer(() => {
  const store = useNotificationsContact();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) store.GetContact(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const contact = store.currentItem as Contact | null;
  if (store.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  if (!contact) return <Alert variant="error">{t('common.notFound')}</Alert>;

  const ct = formatContactType(contact.type ?? '');

  const handleDelete = async () => {
    if (!confirm(t('notifications.deleteContactConfirm'))) return;
    await store.DeleteContact(contact.entity?.id ?? '');
    navigate('/settings/contacts');
  };

  const handleSetPrimary = async () => {
    await store.SetContactPrimary(contact.entity?.id ?? '', {} as never);
    if (id) store.GetContact(id);
  };

  const [showVerifyForm, setShowVerifyForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const handleSendVerification = async () => {
    await store.SendContactVerification(contact.entity?.id ?? '', {} as never);
    setShowVerifyForm(true);
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) return;
    setIsVerifying(true);
    setVerifyError(null);
    try {
      await store.SetContactVerified(contact.entity?.id ?? '', { verification_code: verificationCode.trim() } as never);
      setShowVerifyForm(false);
      setVerificationCode('');
      if (id) store.GetContact(id);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : t('notifications.verificationFailed'));
    } finally {
      setIsVerifying(false);
    }
  };

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono' : ''}`}>{value || t('common.notSet')}</dd>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/settings/contacts" className="text-sm text-primary hover:text-primary/80">{t('notifications.contacts')}</Link>
          <h1 className="text-2xl font-semibold text-foreground mt-1">{contact.value}</h1>
        </div>
        <div className="flex gap-2">
          {!contact.is_verified && <Button variant="secondary" onClick={handleSendVerification}>{t('notifications.sendVerification')}</Button>}
          {!contact.is_verified && <Button variant="secondary" onClick={() => setShowVerifyForm(v => !v)}>{t('notifications.enterCode')}</Button>}
          {!contact.is_primary && <Button variant="secondary" onClick={handleSetPrimary}>{t('notifications.setPrimary')}</Button>}
          <Link to={`/settings/contacts/${contact.entity?.id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {showVerifyForm && !contact.is_verified && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.enterCode')}</CardTitle></CardHeader>
          <CardContent className="flex items-end gap-2">
            <Input
              label={t('notifications.verificationCode')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={handleVerifyCode} isLoading={isVerifying} disabled={!verificationCode.trim()}>
              {t('notifications.verify')}
            </Button>
          </CardContent>
          {verifyError && <p className="px-6 pb-4 text-sm text-destructive">{verifyError}</p>}
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>{t('notifications.contactDetails')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Field label={t('common.id')} value={contact.entity?.id} mono />
            <Field label={t('notifications.contactType')} value={<span className="inline-flex items-center gap-1.5"><NotificationIcon name={ct.icon} className="h-4 w-4" />{t(ct.labelKey)}</span>} />
            <Field label={t('notifications.contactValue')} value={contact.value} mono />
            <Field label={t('notifications.contactPrimary')} value={<Badge variant={contact.is_primary ? 'primary' : 'secondary'}>{contact.is_primary ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('notifications.contactVerified')} value={<Badge variant={contact.is_verified ? 'success' : 'warning'}>{contact.is_verified ? t('notifications.verified') : t('notifications.unverified')}</Badge>} />
            <Field label={t('notifications.contactOwner')} value={contact.owner?.display_name || contact.owner?.entity?.id} />
          </dl>
        </CardContent>
      </Card>

      {contact.metadata && Object.keys(contact.metadata).length > 0 && (
        <Card>
          <CardHeader><CardTitle>{t('common.metadata')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(contact.metadata, null, 2)}</pre></CardContent>
        </Card>
      )}
    </div>
  );
});

export default ContactDetailPage;
